import { Button, Input } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { EditorReorderButtons } from "../editor-reorder-buttons";
import type { useEditorReorder } from "../use-editor-reorder";
import type { MediaTrayItem } from "./media-records";

type MediaTrayRowProps = Readonly<{
  item: MediaTrayItem;
  index: number;
  disabled: boolean;
  altTextMax: number;
  reorder: ReturnType<typeof useEditorReorder>;
  onAltChange(change: Readonly<{ index: number; value: string }>): void;
  onRemove(index: number): void;
}>;

export function MediaTrayRow({
  item,
  index,
  disabled,
  altTextMax,
  reorder,
  onAltChange,
  onRemove,
}: MediaTrayRowProps) {
  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Dragging supplements the keyboard reorder buttons.
    <li {...reorder.dragProps(index)} {...stylex.props(styles.item)}>
      <img
        src={item.kind === "image" ? item.src : (item.poster ?? undefined)}
        alt=""
        {...stylex.props(styles.image)}
      />
      <div {...stylex.props(styles.fields)}>
        <p {...stylex.props(styles.label)}>
          {item.kind === "image" ? "Image" : `Video — ${item.src}`}
        </p>
        <Input
          disabled={disabled}
          value={item.alt}
          maxLength={altTextMax}
          aria-label={`Alt text for media item ${index + 1}`}
          placeholder="Describe what's shown — required"
          onChange={(event) =>
            onAltChange({ index, value: event.target.value })
          }
        />
      </div>
      <div {...stylex.props(styles.actions)}>
        <EditorReorderButtons
          index={index}
          item="media item"
          reorder={reorder}
        />
        <Button
          size="sm"
          variant="quiet"
          aria-label={`Remove media item ${index + 1}`}
          disabled={disabled}
          onClick={() => onRemove(index)}
        >
          Remove
        </Button>
      </div>
    </li>
  );
}

const styles = stylex.create({
  item: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: spacing.space10,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.small,
    backgroundColor: colors.background,
    padding: spacing.space8,
  },
  image: {
    height: 56,
    width: 84,
    flexShrink: 0,
    borderRadius: radii.small,
    objectFit: "cover",
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space4,
    flex: 1,
    minWidth: 150,
  },
  label: {
    margin: 0,
    color: colors.muted,
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    overflowWrap: "anywhere",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.space4,
  },
});
